import type { Transaction } from "@rocicorp/zero";
import type { Schema } from "../generatedDrizzleSchema";
import {
  type AwaitedScopeRow,
  requireResource,
  resolve,
  type ResourceKind,
} from "../mutators/resolve";
import type { ShareableScopeKind } from "./permissions";
import type { ResolvedCustomRole } from "./validation/grantResources";
import type { GrantInput } from "./validation/grantShape";

type ReadTx = Pick<Transaction<Schema>, "run">;

export const grantPrincipalResolve = {
  grantUsers: { kind: "user", from: "grants", field: "userId" },
  grantUserGroups: {
    kind: "userGroup",
    from: "grants",
    field: "userGroupId",
  },
  grantServiceAccounts: {
    kind: "serviceAccount",
    from: "grants",
    field: "serviceAccountId",
  },
} as const;

export const grantRoleResolve = {
  grantCustomRoles: {
    kind: "role",
    from: "grants",
    field: "customRoleId",
  },
} as const;

export const grantCustomRoleResolve = {
  grantCustomRoles: {
    kind: "customRole",
    from: "grants",
    field: "customRoleId",
  },
} as const;

export async function fetchCustomRole(
  tx: ReadTx,
  customRoleId: string,
  tenantId: string
): Promise<ResolvedCustomRole> {
  return requireResource(tx, "customRole", customRoleId, tenantId);
}

export async function fetchGrantResources(
  tx: ReadTx,
  grants: readonly GrantInput[],
  tenantId: string
) {
  return resolve(
    {
      ...grantPrincipalResolve,
      ...grantCustomRoleResolve,
    },
    { args: { grants }, tx, tenantId }
  );
}

const SHAREABLE_RESOURCE_KIND = {
  space: "space",
  connected_app: "connectedApp",
  skill: "skill",
  network: "network",
  service_account: "serviceAccount",
} as const satisfies Record<ShareableScopeKind, ResourceKind>;

type ShareableScopeRow<Kind extends ShareableScopeKind> =
  AwaitedScopeRow[(typeof SHAREABLE_RESOURCE_KIND)[Kind]];

export async function fetchShareableScope<Kind extends ShareableScopeKind>(
  tx: ReadTx,
  scopeId: string,
  scopeKind: Kind,
  tenantId: string
): Promise<ShareableScopeRow<Kind>> {
  return requireResource(
    tx,
    SHAREABLE_RESOURCE_KIND[scopeKind],
    scopeId,
    tenantId
  );
}
